import { getState } from '../state.js';
import { navigate } from '../router.js';
import { imageUrl } from '../config.js';
import { getTranslatedFields, getTranslatedEdgeLabel, translate } from '../lib/i18n.js';
import { getNodeColorFromTags } from '../lib/colorUtils.js';
import { formatDateValue } from '../lib/dateFormat.js';
import { api } from '../api.js';

interface SidebarNode {
  slug: string;
  title: string;
  summary?: string | null;
  content?: string | null;
  image?: string | null;
  date_value?: string | null;
  tags?: { id: number; name: string; color: string }[];
  aliases?: string[];
  translations?: { lang: string; title: string; summary?: string | null; content?: string | null }[];
}

interface SidebarEdge {
  id: number;
  source: string;
  target: string;
  label: string | null;
  reverse_label?: string | null;
  description?: string | null;
  image?: string | null;
  date_value?: string | null;
  translations?: { lang: string; name: string }[];
}

interface ConceptSidebarOptions {
  onTagChange?: (slug: string, tags: { id: number; name: string; color: string }[]) => void;
  onDelete?: (slug: string) => void;
}

interface CrosslinkSidebarOptions {
  onReverse?: (edge: SidebarEdge) => void;
  onDelete?: (edge: SidebarEdge) => void;
}

let cachedTags: { id: number; name: string; color: string }[] | null = null;

async function loadAllTags(): Promise<{ id: number; name: string; color: string }[]> {
  if (cachedTags) return cachedTags;
  try {
    cachedTags = await api.getTags() as { id: number; name: string; color: string }[];
    return cachedTags;
  } catch {
    return [];
  }
}

function renderQuickTags(contentEl: HTMLElement, slug: string, node: SidebarNode, onTagChange?: ConceptSidebarOptions['onTagChange']): void {
  const container = contentEl.querySelector('#sidebar-quick-tags');
  if (!container) return;

  loadAllTags().then(allTags => {
    if (!allTags.length) {
      container.innerHTML = '<span style="color: var(--dim); font-size: 0.7rem;">No tags defined</span>';
      return;
    }

    const assignedIds = new Set((node.tags || []).map(tg => tg.id));

    container.innerHTML = allTags.map(tag => `
      <button class="quick-tag-btn ${assignedIds.has(tag.id) ? 'quick-tag-btn--active' : ''}"
        data-tag-id="${tag.id}" data-tag-name="${tag.name}"
        style="--tag-btn-color: ${tag.color};"
        title="${tag.name}">${tag.name}</button>
    `).join('');

    for (const btn of container.querySelectorAll('.quick-tag-btn') as NodeListOf<HTMLButtonElement>) {
      btn.addEventListener('click', async () => {
        const tagId = parseInt(btn.dataset.tagId!);
        const isActive = btn.classList.contains('quick-tag-btn--active');
        btn.disabled = true;
        try {
          if (isActive) {
            await api.removeTagFromConcept(slug, tagId);
            node.tags = (node.tags || []).filter(tg => tg.id !== tagId);
          } else {
            const tag = await api.assignTagToConcept(slug, tagId) as { id: number; name: string; color: string };
            if (!node.tags) node.tags = [];
            node.tags.push(tag);
          }
          const badge = contentEl.querySelector('.graph-sidebar__state') as HTMLElement | null;
          if (badge) {
            const newColor = getNodeColorFromTags(node.tags || [], 'hex') as string;
            badge.style.color = newColor;
            badge.style.borderColor = newColor;
            badge.textContent = (node.tags || []).length > 0 ? (node.tags || []).map(tg => tg.name).join(', ') : translate('state.untagged', 'untagged');
          }
          if (onTagChange) onTagChange(slug, node.tags || []);
          renderQuickTags(contentEl, slug, node, onTagChange);
        } catch (err) {
          btn.textContent = '!';
          btn.title = (err as Error).message;
        }
        btn.disabled = false;
      });
    }
  });
}

export function showConceptInSidebar(sidebar: HTMLElement, node: SidebarNode, options: ConceptSidebarOptions = {}): void {
  const { onTagChange, onDelete } = options;

  const { title, summary } = getTranslatedFields(node);
  const { user } = getState();
  const tagColor = getNodeColorFromTags(node.tags || [], 'hex') as string;
  const dateStr = formatDateValue(node.date_value);

  const isAdmin = user && user.is_admin;

  const deleteHtml = isAdmin && onDelete
    ? `<button class="btn btn--danger btn--small" id="delete-concept-btn" style="margin-top: 0.5rem;">${translate('graph.delete_concept', 'Delete concept')}</button>
       <div class="admin-section__feedback" id="delete-concept-feedback" style="margin-top: 0.5rem;"></div>`
    : '';

  const adminTagsHtml = isAdmin
    ? `<div class="sidebar-quick-tags" style="margin-top: 0.75rem;">
         <div id="sidebar-quick-tags" style="display: flex; flex-wrap: wrap; gap: 0.25rem;"></div>
       </div>`
    : '';

  let aliasesHtml = '';
  if (node.aliases && node.aliases.length > 0) {
    aliasesHtml = `<div class="concept-translations" style="margin-top: 0.5rem;">
      <span class="concept-translations__label">${translate('concept.also_known_as', 'also known as')}</span>
      ${node.aliases.map(a => `<span class="concept-translations__item">${a}</span>`).join('')}
    </div>`;
  }

  const conceptImageHtml = node.image
    ? `<img class="graph-sidebar__image" src="${imageUrl(node.image)}" alt="${title}">`
    : '';

  const viewLink = `<a class="btn btn--ghost btn--small" data-link="/concept/${node.slug}" style="margin-top: 0.5rem;">${translate('graph.view_concept', 'View concept')} &rarr;</a>`;

  sidebar.style.display = 'block';
  const content = sidebar.querySelector('.graph-sidebar__content') as HTMLElement;
  content.innerHTML = `
    <span class="graph-sidebar__state" style="color: ${tagColor}; border-color: ${tagColor};">${node.tags && node.tags.length > 0 ? node.tags.map(tg => tg.name).join(', ') : translate('state.untagged', 'untagged')}</span>
    <h3 class="graph-sidebar__title">${title}</h3>
    ${dateStr ? `<span style="font-family: var(--font-system); font-size: 0.75rem; color: var(--dim);">${dateStr}</span>` : ''}
    ${aliasesHtml}
    ${conceptImageHtml}
    ${adminTagsHtml}
    ${summary
      ? `<p class="graph-sidebar__summary">${summary}</p>`
      : `<p class="graph-sidebar__empty">${translate('graph.no_summary', 'No summary yet.')}</p>`
    }
    <div style="margin-top: 1.25rem; display: flex; flex-direction: column; gap: 0.25rem;">
      ${viewLink}
      ${deleteHtml}
    </div>
  `;

  if (isAdmin) {
    renderQuickTags(content, node.slug, node, onTagChange);
  }

  const viewBtn = content.querySelector('[data-link]') as HTMLElement | null;
  if (viewBtn) {
    viewBtn.addEventListener('click', (e) => {
      e.preventDefault();
      navigate(viewBtn.dataset.link!);
    });
  }

  const deleteBtn = content.querySelector('#delete-concept-btn');
  if (deleteBtn && onDelete) {
    deleteBtn.addEventListener('click', async () => {
      if (!confirm(`Delete concept "${title}"? This cannot be undone.`)) return;
      const feedbackEl = content.querySelector('#delete-concept-feedback');
      try {
        await api.deleteConcept(node.slug);
        onDelete(node.slug);
      } catch (err) {
        if (feedbackEl) {
          feedbackEl.textContent = (err as Error).message;
          feedbackEl.className = 'admin-section__feedback form-error';
        }
      }
    });
  }
}

export function showCrosslinkInSidebar(sidebar: HTMLElement, edge: SidebarEdge, nodes: SidebarNode[], options: CrosslinkSidebarOptions = {}): void {
  const { onReverse, onDelete } = options;

  if (!edge) return;

  const sourceNode = nodes.find(n => n.slug === edge.source);
  const targetNode = nodes.find(n => n.slug === edge.target);
  const sourceName = sourceNode ? getTranslatedFields(sourceNode).title : edge.source;
  const targetName = targetNode ? getTranslatedFields(targetNode).title : edge.target;

  const { user } = getState();
  const isAdmin = user && user.is_admin;

  const adminActionsHtml = isAdmin
    ? `<div style="margin-top: 1rem; display: flex; gap: 0.5rem; flex-wrap: wrap;">
         <button class="btn btn--small" id="reverse-crosslink-btn">${translate('graph.reverse_crosslink', 'Reverse crosslink')}</button>
         <button class="btn btn--danger btn--small" id="delete-crosslink-btn">${translate('graph.delete_crosslink', 'Delete crosslink')}</button>
       </div>
       <div class="admin-section__feedback" id="crosslink-feedback" style="margin-top: 0.5rem;"></div>`
    : '';

  const edgeDateFormatted = formatDateValue(edge.date_value);
  const edgeDateHtml = edgeDateFormatted
    ? `<div style="font-family: var(--font-system); font-size: 0.8rem; color: var(--dim); margin-top: 0.5rem;">${edgeDateFormatted}</div>`
    : '';

  const edgeImageHtml = edge.image
    ? `<img class="graph-sidebar__image" src="${imageUrl(edge.image)}" alt="crosslink">`
    : '';

  sidebar.style.display = 'block';
  const content = sidebar.querySelector('.graph-sidebar__content') as HTMLElement;
  content.innerHTML = `
    <h3 class="graph-sidebar__title">
      ${sourceName}
      ${edge.label ? `<span class="graph-sidebar__relation">${getTranslatedEdgeLabel(edge)}</span>` : ''}
      ${targetName}
    </h3>
    ${edge.description
      ? `<p class="graph-sidebar__description">${edge.description}</p>`
      : `<p class="graph-sidebar__empty">${translate('graph.no_description', 'No description yet.')}</p>`
    }
    ${edgeImageHtml}
    ${edgeDateHtml}
    ${adminActionsHtml}
  `;

  const feedbackEl = content.querySelector('#crosslink-feedback');

  const reverseBtn = content.querySelector('#reverse-crosslink-btn');
  if (reverseBtn && edge.id) {
    reverseBtn.addEventListener('click', async () => {
      try {
        const cl = await api.lookupCrosslinkById(edge.id) as { reverse: number };
        await api.updateCrosslink(edge.id, { reverse: cl.reverse ? 0 : 1 });
        if (onReverse) onReverse(edge);
      } catch (err) {
        if (feedbackEl) {
          feedbackEl.textContent = (err as Error).message;
          feedbackEl.className = 'admin-section__feedback form-error';
        }
      }
    });
  }

  const deleteBtn = content.querySelector('#delete-crosslink-btn');
  if (deleteBtn && edge.id) {
    deleteBtn.addEventListener('click', async () => {
      if (!confirm(`Delete crosslink between "${sourceName}" and "${targetName}"?`)) return;
      try {
        await api.deleteCrosslink(edge.id);
        if (onDelete) onDelete(edge);
      } catch (err) {
        if (feedbackEl) {
          feedbackEl.textContent = (err as Error).message;
          feedbackEl.className = 'admin-section__feedback form-error';
        }
      }
    });
  }
}
